import { envConfig } from '#constants/envConfig';

const STORAGE_KEY = 'locale';
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/**
 * Locale persistence for the native shell.
 *
 * On the web the choice is stored by the server (`utils/updateLocale` sets the cookie
 * via Set-Cookie) and read back synchronously on boot in `src/client.tsx`. The native
 * shell has no server, so it writes the same cookie itself — the WebView has a normal
 * cookie jar on the `https://localhost` app origin, which keeps the boot path unchanged.
 *
 * The cookie alone is not enough to rely on: Android only flushes the WebView cookie
 * store periodically, so a choice made just before the app is killed can be lost. The
 * value is therefore mirrored into Capacitor Preferences, which writes immediately, and
 * reconciled on the next cold start (see hooks/useNativeLocaleSync).
 *
 * Gated on IS_NATIVE so the @capacitor/preferences import is tree-shaken out of the web
 * bundle.
 */
export const setLocaleCookie = (locale: string) => {
  document.cookie = `${STORAGE_KEY}=${encodeURIComponent(locale)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}`;
};

export const readLocaleCookie = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)locale=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const setNativeLocale = async (locale: string) => {
  if (!envConfig.IS_NATIVE) {
    return;
  }

  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: STORAGE_KEY, value: locale });
  } catch {
    // Storage is best-effort: the cookie already carries the choice for this session.
  }
};

export const getNativeLocale = async (): Promise<string | null> => {
  if (!envConfig.IS_NATIVE) {
    return null;
  }

  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    return value ?? null;
  } catch {
    return null;
  }
};
